import React, { Dispatch } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  Box,
  Table,
  TableBody,
  TableCell as TC,
  TableHeader,
  TableRow,
} from 'grommet';
import ReactTooltip from 'react-tooltip';
import { Text } from '../../../components/Text';
import { CheckBox } from '../../../components/CheckBox';
import shortenAddress from '../../../utils/shortenAddress';
import { Paper } from '../../../components/Paper';
import { Link } from '../../../components/Link';
import { BEACONCHAIN_URL, TICKER_NAME } from '../../../utils/envVars';
import { ConsolidationKeyInterface } from '../../../store/reducers/consolidate';
import { Action, ActionTypes } from '../../../store/actions';
import { TxStatus } from './TransactionStatus';
import { TagIndicator, GreyTag } from '../../../components/TagIndicator';
import { TransactionStatus } from '../../../store/actions/depositFileActions';

const FakeLink = styled.span`
  color: blue;
  text-decoration: underline;
  cursor: pointer;
  display: inline;
`;

const TableCell = styled(TC)`
  display: flex;
  flex-wrap: wrap;
  text-align: center;
  align-content: center;
  justify-content: space-around;
`;

const ValidatorTable: React.FC<{
  validators: ConsolidationKeyInterface[];
  dispatch: Dispatch<Action>;
  handleConnect: () => void;
}> = ({ validators, dispatch, handleConnect }) => {
  const validatorRows = React.useMemo(() => {
    return validators.map(validator => {
      if (validator.status !== 'active_ongoing') return <></>;
      return (
        <React.Fragment key={validator.pubkey}>
          <TableRow>
            <TableCell scope="col" border="bottom">
              <Link
                primary
                to={`${BEACONCHAIN_URL}/validator/${validator.pubkey}`}
              >
                #{validator.validatorindex}
              </Link>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Link
                primary
                to={`${BEACONCHAIN_URL}/validator/${validator.pubkey}`}
              >
                {shortenAddress(validator.pubkey)}
              </Link>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <TagIndicator
                value={`Type ${validator.withdrawalcredentials[3]}`}
                colors={GreyTag}
              />
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Text>
                {validator.effectivebalance / 10 ** 9} {TICKER_NAME}
              </Text>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <TxStatus
                transactionStatus={validator.transactionStatus}
                txHash={validator.txHash}
              />
            </TableCell>
            <TableCell scope="col" border="bottom">
              <CheckBox
                isActive={validator.isSource}
                isSuccess={
                  validator.transactionStatus === TransactionStatus.SUCCEEDED
                }
                onClick={() =>
                  dispatch({
                    type: ActionTypes.updateSourceValidator,
                    payload: validator.pubkey,
                  })
                }
              />
            </TableCell>
            <TableCell scope="col" border="bottom">
              <CheckBox
                isActive={validator.isTarget}
                isSuccess={false} // show success only on source validators
                onClick={() =>
                  dispatch({
                    type: ActionTypes.updateTargetValidator,
                    payload: validator.pubkey,
                  })
                }
              />
            </TableCell>
          </TableRow>
        </React.Fragment>
      );
    });
  }, [validators, dispatch]);

  return validators.length > 0 ? (
    <Paper
      style={{
        marginTop: '1rem',
        maxHeight: '600px',
        overflowY: 'scroll',
        borderRadius: '24px',
      }}
    >
      <ReactTooltip />
      <Table>
        <TableHeader style={{ marginBottom: '8px' }}>
          <TableRow>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="Index" />
              </Text>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="Public key" />
              </Text>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="Credentials type" />
              </Text>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="Effective balance" />
              </Text>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="Transaction Status" />
              </Text>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="Source validator" />
              </Text>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="Target validator" />
              </Text>
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>{validatorRows}</TableBody>
      </Table>
    </Paper>
  ) : (
    <Box align="center" justify="center" className="mt40">
      <Text weight={600}>
        <FormattedMessage defaultMessage="No validators found for your connected wallet" />
      </Text>
      <Text className="mt20">
        <FormattedMessage
          defaultMessage="You can {changeYourWallet} to load validators for a different address."
          values={{
            changeYourWallet: (
              <FakeLink onClick={handleConnect}>
                <FormattedMessage defaultMessage="change your wallet" />
              </FakeLink>
            ),
          }}
          description="{changeYourWallet} is a link labeled 'change your wallet'"
        />
      </Text>
    </Box>
  );
};

export default ValidatorTable;
